import {useEffect, useMemo} from 'react';
import {Control, UseFormGetValues, UseFormSetValue, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {useIsFreeTariff, userSelector, hasTariffFlag} from 'entities/user';
import {TariffBooleanLimit} from 'entities/tariff';
import {BODY_DEPENDENT_FIELD_KEYS} from 'features/http/lib/batchEditFields';
import {
    buildCheckFeatures,
    getBodyFieldsToReset,
    IBodyDependentFieldState,
    methodAllowsBody,
    shouldResetBodyFields,
} from 'features/http/lib/checkboxBlockView';

import {useBasicAuth} from './useBasicAuth';

interface IUseCheckboxBlockParams {
    control: Control<any>,
    getValues: UseFormGetValues<any>,
    setValue: UseFormSetValue<any>,
    activeKeys?: string[],
}

export const useCheckboxBlock = ({
    control,
    getValues,
    setValue,
    activeKeys,
}: IUseCheckboxBlockParams) => {
    const user = useSelector(userSelector);
    const freeTariff = useIsFreeTariff();
    const selective = !!activeKeys;
    const isActive = (key: string) => !selective || (activeKeys?.includes(key) ?? false);

    const [
        enabledBasicAuth,
        isParseErrorsChecked,
        method,
        isContentMonitoringChecked,
        isScreenshotChecked,
    ] = useWatch({
        control,
        name: ['auth_enabled', 'parse_errors', 'method', 'monitor_content_enabled', 'screenshot_on_error'],
        defaultValue: [
            getValues('auth_enabled'),
            getValues('parse_errors'),
            getValues('method') || 'HEAD',
            getValues('monitor_content_enabled'),
            getValues('screenshot_on_error'),
        ],
    });
    const isParseErrorsJson = getValues('parse_errors_type') === 'json';
    useBasicAuth(control, enabledBasicAuth);

    const bodyMethodDisabled = selective && !methodAllowsBody(method);
    const parseActive = isActive('parse_errors');
    const contentActive = isActive('monitor_content_enabled');
    const screenshotActive = isActive('screenshot_on_error');

    const checkFeatures = useMemo(() => buildCheckFeatures(), []);

    const handleBodyFieldClick = (isChecked: boolean) => () => {
        if (selective) return;
        if (method === 'HEAD' && !isChecked) {
            setValue('method', 'GET');
        }
    };

    useEffect(() => {
        if (!shouldResetBodyFields(selective, bodyMethodDisabled, method)) {
            return;
        }

        const state: Record<string, IBodyDependentFieldState> = {
            parse_errors: {isFieldActive: parseActive, isChecked: isParseErrorsChecked},
            monitor_content_enabled: {isFieldActive: contentActive, isChecked: isContentMonitoringChecked},
            screenshot_on_error: {isFieldActive: screenshotActive, isChecked: isScreenshotChecked},
        };

        getBodyFieldsToReset(BODY_DEPENDENT_FIELD_KEYS, state).forEach((key) => setValue(key, false));
    }, [
        selective,
        bodyMethodDisabled,
        parseActive,
        contentActive,
        screenshotActive,
        isContentMonitoringChecked,
        isParseErrorsChecked,
        isScreenshotChecked,
        method,
        setValue,
    ]);

    return {
        isTariffEnabled: (flag: TariffBooleanLimit) => hasTariffFlag(user, flag),
        freeTariff,
        selective,
        isActive,
        enabledBasicAuth,
        isParseErrorsChecked,
        isContentMonitoringChecked,
        isScreenshotChecked,
        isParseErrorsJson,
        bodyMethodDisabled,
        screenshotActive,
        checkFeatures,
        handleBodyFieldClick,
    };
};

export default useCheckboxBlock;
